import React from "react";
import { useParams, useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Calendar,
  MapPin,
  Users,
  Target,
  Music,
  ChevronLeft,
  User,
  Building,
  Printer,
  Edit,
  Mic2,
  Globe
} from "lucide-react";
import { type PlanWithDetails } from "@shared/schema";
import { PlanMapView } from "@/components/plan-map-view";
import { PlanCalendar } from "@/components/plan-calendar";
import { PlanCalendarGrid } from "@/components/plan-calendar-grid";
import { PlanDeliverables } from "@/components/plan-deliverables";
import { formatPrice } from "@/lib/package-config";
import { calculatePotentialAttendees, calculateAvgAttendeesPerShow } from "@shared/career-stage-attendance";
import { PlanStorage } from "@/lib/plan-storage";
import { useAuth } from "@/lib/auth";
import { usePricingVisibility } from "@/lib/pricing-visibility";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/navbar";

// Helper Functions
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase();
};

const formatCampaignDateRange = (startDate?: string | Date, endDate?: string | Date) => {
  if (!startDate) return 'No dates set';
  const start = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (!endDate) return `Starting ${start}`;
  const end = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${start} - ${end}`;
};

// Convert showPackages from server format to component format
const convertShowPackagesFormat = (plan: PlanWithDetails) => {
  if (!plan.shows) return {};

  // Try top-level first (where we actually save it), then fall back to planStructure
  const showPackages = (plan as any).showPackages || (plan.careerStageBreakdown as any)?.planStructure?.showPackages || [];

  return plan.shows.reduce((acc, show) => {
    const pkg = showPackages.find((p: any) => p.showId === show.id);
    const tier = pkg?.packageTier || 'set-video';

    acc[show.id] = {
      showId: show.id,
      setCore: tier === 'set-core',
      setAudio: tier === 'set-audio',
      setVideo: tier === 'set-video'
    };
    return acc;
  }, {} as Record<number, { showId: number, setCore: boolean, setAudio: boolean, setVideo: boolean }>);
};

export default function PlanDetail() {
  const { id } = useParams<{ id: string }>();
  const planId = parseInt(id || "0");
  const { user } = useAuth();
  const { isPricingVisible } = usePricingVisibility();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const { data: plan, isLoading, error } = useQuery<PlanWithDetails>({
    queryKey: [`/api/plans/${planId}`],
    enabled: !!planId && planId > 0,
    retry: 3,
    retryDelay: 1000,
  });

  const handleEditPlan = () => {
    if (!plan || !plan.shows) return;

    // Clear existing shows
    PlanStorage.clearShows(user?.id);

    // Extract saved package assignments from plan data
    const savedPackages = (plan as any).showPackages || (plan.careerStageBreakdown as any)?.planStructure?.showPackages || [];

    // Create lookup map: showId -> {packageTier, customPrice}
    const packageMap = savedPackages.reduce((acc: any, pkg: any) => {
      acc[pkg.showId] = {
        tier: pkg.packageTier,
        price: pkg.customPrice
      };
      return acc;
    }, {});

    // Load shows into PlanStorage WITH their package assignments
    plan.shows.forEach(show => {
      const packageInfo = packageMap[show.id];
      if (packageInfo) {
        PlanStorage.addShow(show, user?.id, packageInfo.tier, packageInfo.price);
      } else {
        // Fallback: add show without package (will default to all-access)
        PlanStorage.addShow(show, user?.id);
      }
    });

    // Navigate to plans page, Create tab
    setLocation('/plans');

    toast({
      title: "Plan loaded into builder",
      description: `Loaded ${plan.shows.length} show(s). You can now edit and save changes.`,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-6xl pt-24">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8 max-w-6xl pt-24">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Plan Not Found</h2>
            <p className="text-gray-600 mb-6">
              {error ? `Error: ${error.message}` : 'The plan you\'re looking for doesn\'t exist or has been removed.'}
            </p>
            <Link href="/plans">
              <Button>
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Plans
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Extract preparer, brand, audience, campaign details from plan structure
  const planStructure = (plan.careerStageBreakdown as any)?.planStructure || {};
  const preparerDetails = planStructure.preparerDetails || {};
  const brandDetails = planStructure.brandDetails || null;
  const audienceProfile = planStructure.audienceProfile || null;
  const campaignDetails = planStructure.campaignDetails || null;

  return (
    <div className="min-h-screen bg-neutral-50">
      <Navbar />

      {/* Header with Action Buttons */}
      <div className="sticky top-16 z-30 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-between h-16">
            <Link href="/plans">
              <Button variant="ghost" size="sm">
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back to Plans
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditPlan}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Plan
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.print()}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 max-w-6xl space-y-8">
        {/* Top Section - Who's Preparing and Plan Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Plan Prepared By */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Plan Prepared By</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between">
                  {/* Personal Information - Left Side */}
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={preparerDetails.profileImageUrl} alt={preparerDetails.name} />
                      <AvatarFallback className="text-sm">
                        {preparerDetails.name ? getInitials(preparerDetails.name) : <User className="w-4 h-4" />}
                      </AvatarFallback>
                    </Avatar>
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium text-blue-900">{preparerDetails.name || "Not provided"}</p>
                      <p className="text-xs text-blue-700">{preparerDetails.jobTitle || "Not provided"}</p>
                      <p className="text-xs text-blue-700">{preparerDetails.email || "Not provided"}</p>
                    </div>
                  </div>

                  {/* Company Information - Right Side */}
                  <div className="flex items-center gap-3">
                    <div className="text-right space-y-0.5">
                      <p className="text-sm font-medium text-blue-900">{preparerDetails.company || "Not provided"}</p>
                    </div>
                    {preparerDetails.companyLogoUrl && (
                      <div className="w-10 h-10 flex items-center justify-center bg-white rounded border">
                        <img src={preparerDetails.companyLogoUrl} alt="Company logo" className="max-w-8 max-h-8 object-contain" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Plan Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Plan Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Plan Name */}
              <div>
                <p className="text-sm text-gray-600 mb-1">Plan Name</p>
                <p className="font-medium text-lg">{plan.campaignName}</p>
              </div>

              {/* Brand */}
              {brandDetails && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Brand</p>
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{brandDetails.name}</span>
                  </div>
                </div>
              )}

              {/* Audience Profile */}
              {audienceProfile && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Audience Persona</p>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{audienceProfile.profileName}</span>
                  </div>
                </div>
              )}

              {/* Campaign */}
              {campaignDetails && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Campaign</p>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{campaignDetails.name}</span>
                  </div>
                </div>
              )}

              {/* Campaign Details - Show additional info when campaign exists */}
              {campaignDetails && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                  <h4 className="text-sm font-medium text-gray-700">Campaign Details</h4>

                  {/* Campaign Duration */}
                  {campaignDetails.startDate && (
                    <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-900">Duration</span>
                      </div>
                      <div className="text-sm font-bold text-blue-700">
                        {formatCampaignDateRange(campaignDetails.startDate, campaignDetails.endDate)}
                      </div>
                    </div>
                  )}

                  {/* Geographic Reach */}
                  {plan.shows && plan.shows.length > 0 && (
                    <div className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium text-green-900">Geographic Reach</span>
                      </div>
                      <div className="text-sm font-bold text-green-700">
                        {[...new Set(plan.shows.map(show => show.venue.market?.name || 'Unknown'))].filter(m => m !== 'Unknown').length} markets
                      </div>
                    </div>
                  )}

                  {/* Campaign Objective */}
                  {campaignDetails.campaignObjective && (
                    <div className="flex items-center justify-between bg-purple-50 rounded-lg p-3">
                      <div className="flex items-center gap-2">
                        <Target className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-900">Objective</span>
                      </div>
                      <div className="text-sm font-bold text-purple-700 capitalize">
                        {campaignDetails.campaignObjective}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Dashboard Layout - Overview, Timeline, and Deliverables */}
        {plan.shows && plan.shows.length > 0 && (
          <div className="space-y-6">
            {/* Overview Section - Plan Summary + Map View */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Plan Summary - 1/3 width */}
              <Card className="plan-summary-card border-pink-200/50 overflow-hidden flex flex-col h-full shadow-lg">
                {/* Hero Price Section with Gradient */}
                <div className="bg-gradient-to-br from-[#FA017C] to-[#0240FB] p-6 text-white relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/10 to-transparent animate-pulse opacity-50"></div>
                  <div className="relative z-10">
                    {isPricingVisible ? (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-sm font-medium opacity-90">Total Investment</p>
                        </div>
                        <p className="text-5xl font-bold mb-3">
                          {formatPrice(parseFloat(plan.totalBudget || '0'))}
                        </p>
                        <div className="pt-3 border-t border-white/20">
                          <p className="text-lg font-medium">
                            {formatPrice(Math.round(parseFloat(plan.totalBudget || '0') / Math.max(plan.shows.length, 1)))} <span className="text-sm opacity-80">avg per show</span>
                          </p>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <p className="text-sm font-medium opacity-90 mb-2">Plan Summary</p>
                        <p className="text-2xl font-bold">Contact for</p>
                        <p className="text-2xl font-bold">Pricing</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stat Grid */}
                <CardContent className="p-6 space-y-3 flex-1">
                  {/* Row 1: Shows, Artists, Markets, States */}
                  <div className="plan-summary-stats grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Shows Card */}
                    <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-blue-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative z-10 text-center">
                        <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                          <Mic2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <p className="text-2xl font-bold text-blue-900">{plan.shows.length}</p>
                        <p className="text-xs text-blue-600 font-medium">Shows</p>
                      </div>
                    </div>

                    {/* Artists Card */}
                    <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-pink-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative z-10 text-center">
                        <div className="w-9 h-9 bg-pink-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-pink-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                          <Music className="h-5 w-5 text-pink-600" />
                        </div>
                        <p className="text-2xl font-bold text-pink-900">
                          {[...new Set(plan.shows.map(show => show.artist.id))].length}
                        </p>
                        <p className="text-xs text-pink-600 font-medium">Artists</p>
                      </div>
                    </div>

                    {/* Markets Card */}
                    {(() => {
                      const uniqueMarkets = [...new Set(
                        plan.shows
                          .map(show => show.venue.market?.name || null)
                          .filter(Boolean)
                      )].length;

                      return (
                        <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-purple-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative z-10 text-center">
                            <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-purple-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                              <MapPin className="h-5 w-5 text-purple-600" />
                            </div>
                            <p className="text-2xl font-bold text-purple-900">{uniqueMarkets}</p>
                            <p className="text-xs text-purple-600 font-medium">{uniqueMarkets === 1 ? 'Market' : 'Markets'}</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* States Card */}
                    {(() => {
                      const uniqueStates = [...new Set(
                        plan.shows
                          .map(show => show.venue.state || null)
                          .filter(Boolean)
                      )].length;

                      return (
                        <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-t-4 border-cyan-500 shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                          <div className="relative z-10 text-center">
                            <div className="w-9 h-9 bg-cyan-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-cyan-200 group-hover:scale-110 transition-all duration-300 mx-auto">
                              <Globe className="h-5 w-5 text-cyan-600" />
                            </div>
                            <p className="text-2xl font-bold text-cyan-900">{uniqueStates}</p>
                            <p className="text-xs text-cyan-600 font-medium">{uniqueStates === 1 ? 'State' : 'States'}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Row 2: Duration Card (Full Width) */}
                  {(() => {
                    const sortedShows = [...plan.shows].sort((a, b) =>
                      new Date(a.date).getTime() - new Date(b.date).getTime()
                    );
                    const firstDate = new Date(sortedShows[0].date);
                    const lastDate = new Date(sortedShows[sortedShows.length - 1].date);
                    const days = Math.ceil((lastDate.getTime() - firstDate.getTime()) / (1000 * 60 * 60 * 24));

                    const formatDate = (date: Date) => {
                      const month = date.toLocaleDateString('en-US', { month: 'short' });
                      const day = date.getDate().toString().padStart(2, '0');
                      const year = date.getFullYear();
                      return `${month} ${day}, ${year}`;
                    };

                    return (
                      <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-amber-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center group-hover:bg-amber-200 group-hover:scale-110 transition-all duration-300">
                              <Calendar className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-amber-900">{days}</p>
                              <p className="text-xs text-amber-600 font-medium">{days === 1 ? 'Day' : 'Days'}</p>
                            </div>
                          </div>
                          <div className="text-right space-y-0.5">
                            <p className="text-xs font-medium text-amber-900">
                              <span className="text-amber-600">Start:</span> {formatDate(firstDate)}
                            </p>
                            <p className="text-xs font-medium text-amber-900">
                              <span className="text-amber-600">End:</span> {formatDate(lastDate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Row 3: Potential Live Attendees Card (Full Width) */}
                  {(() => {
                    const totalAttendees = calculatePotentialAttendees(plan.shows);
                    const avgAttendees = calculateAvgAttendeesPerShow(plan.shows);

                    return (
                      <div className="group relative bg-white/60 backdrop-blur-sm rounded-lg p-3 border-l-4 border-green-500 shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        <div className="relative z-10 flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center group-hover:bg-green-200 group-hover:scale-110 transition-all duration-300">
                              <Users className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold text-green-900">{totalAttendees.toLocaleString()}</p>
                              <p className="text-xs text-green-600 font-medium whitespace-nowrap">Live Attendees</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-green-900">{avgAttendees.toLocaleString()}</p>
                            <p className="text-xs text-green-600 font-medium whitespace-nowrap">avg per show</p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Map View - 2/3 width */}
              <div className="plan-map-view lg:col-span-2 flex flex-col h-full">
                <PlanMapView
                  shows={plan.shows}
                  showPackages={convertShowPackagesFormat(plan)}
                />
              </div>
            </div>

            {/* Timeline Section - Full Width */}
            <PlanCalendar
              shows={plan.shows}
              showPackages={convertShowPackagesFormat(plan)}
              campaignStart={campaignDetails?.startDate || undefined}
              campaignEnd={campaignDetails?.endDate || undefined}
            />

            {/* Calendar Grid Section - Full Width */}
            <PlanCalendarGrid
              shows={plan.shows}
              showPackages={convertShowPackagesFormat(plan)}
              campaignStart={campaignDetails?.startDate || undefined}
              campaignEnd={campaignDetails?.endDate || undefined}
            />

            {/* Deliverables Section - Full Width */}
            <PlanDeliverables
              showPackages={convertShowPackagesFormat(plan)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
